/**
 * Builder `bricks()` + controller — implementación R1 (contrato §3.2).
 *
 * `bricks.create` construye la URL del iframe (con query params públicos y
 * `embedded=1`), crea el `<iframe>` seguro (sandbox + referrerpolicy), instancia
 * el `Messenger` (handshake ready→init) y, según `mode`, delega el montaje al
 * modo correspondiente (`embedded` inline con auto-resize; `modal` overlay con
 * a11y — implementado por R2).
 *
 * El controller devuelto expone `unmount/open/close/update`. Para
 * `mode === 'embedded'`, `open/close` son no-op (no hay overlay). `update`
 * envía `finva:update` al iframe, salvo cuando cambia `brand` (afecta la URL),
 * en cuyo caso **reinicia el handshake** del messenger y recarga el iframe con
 * los nuevos query params — sin destruir el messenger, para que las
 * suscripciones del mount (`finva:resize`/`finva:close`) sobrevivan la recarga.
 *
 * Los callbacks se leen *live* desde `internals.settings` (no se capturan al
 * cablear), así cualquier `update` que cambie callbacks se refleja de inmediato
 * sin re-cablear.
 */
import type { BrickController, BrickSettings } from './api';
import type { Finva } from './finva';
import type { InitPayload, UpdatePayload } from '../transport/protocol';
import { productEqual } from '../transport/protocol';
import { Messenger } from '../transport/messenger';
import { mountEmbedded, type EmbeddedMount } from '../modes/embedded';
import { mountModal, type ModalMount } from '../modes/modal';
import { createSecureIframe, encodeQueryParam, requireContainer } from '../util/dom';
import { CAMPAIGN_KEYS, getCampaignParams } from '../util/campaign';
import { mergeSettings, normalizeSettings, analyticsEqual, flowEqual, flowsEqual, type NormalizedCallbacks, type NormalizedSettings } from '../util/options';

/**
 * Contrato común a ambos modos de montaje. `open/close` son opcionales porque
 * el modo `embedded` no las provee (no hay overlay). `ModalMount` declara
 * `open()/close()` obligatorias y satisface este tipo por tipado estructal.
 */
export interface BrickMount {
  unmount(): void;
  open?(): void;
  close?(): void;
}

export interface Bricks {
  create(
    type: 'application',
    containerId: string,
    settings: BrickSettings,
  ): BrickController;
}

/**
 * Crea el builder `bricks()` ligado a una instancia `Finva` (publicKey, locale,
 * origin). El builder guarda una referencia débil a la instancia para que
 * `finva.open()/close()` puedan operar sobre el último brick modal registrado.
 */
export function createBricks(finva: Finva): Bricks {
  return {
    create(type, containerId, settings) {
      if (type !== 'application') {
        throw new Error(`Finva: tipo de brick no soportado "${type}" (use "application")`);
      }
      return createApplicationBrick(finva, containerId, settings);
    },
  };
}

/** Registro interno de bricks modales para `finva.open()/close()` (azúcar §3.2). */
export interface ModalBrickHandle {
  open(): void;
  close(): void;
}

interface BrickInternals {
  settings: NormalizedSettings;
  messenger: Messenger;
  mount: BrickMount | null; // embedded o modal (modal provee open/close).
  iframe: HTMLIFrameElement;
  container: HTMLElement;
  unmounted: boolean;
}

function createApplicationBrick(
  finva: Finva,
  containerId: string,
  rawSettings: BrickSettings,
): BrickController {
  const settings = normalizeSettings(rawSettings);
  const container = requireContainer(containerId);

  const initPayload: InitPayload = buildInitPayload(finva, settings);
  const iframeSrc = buildIframeUrl(finva.origin, initPayload, settings.analytics, {
    flow: settings.flow,
    availableFlows: settings.availableFlows,
  });
  const iframe = createSecureIframe(iframeSrc);

  const messenger = new Messenger({
    iframe,
    allowedOrigins: [finva.origin],
    initPayload,
  });
  messenger.connect();

  let internals: BrickInternals = {
    settings,
    messenger,
    mount: null,
    iframe,
    container,
    unmounted: false,
  };

  if (settings.mode === 'embedded') {
    internals.mount = mountEmbedded({ container, iframe, messenger });
  } else {
    // mode === 'modal' — implementado por R2 (Ola 2). Monta el overlay y
    // registra el handle en `finva._registerModalBrick` para que
    // `finva.open()/close()` operen sobre este brick (azúcar §3.2).
    const modal = mountModal({ container, iframe, messenger });
    internals.mount = modal;
    finva._registerModalBrick(modal); // ModalMount satisface ModalBrickHandle
  }

  // Callbacks leídos *live* desde `internals.settings`: cualquier `update`
  // (incluido uno que cambie callbacks sin cambiar `brand`) se refleja sin
  // re-cablear handlers. Se cablea tras asignar `internals` para que el getter
  // tenga el binding disponible cuando dispare un mensaje.
  wireCallbacks(messenger, () => internals.settings);

  // En modo embedded no se registra ningún brick modal: finva.open()/close()
  // no aplican (siguen siendo no-op con aviso por consola).

  return {
    unmount(): void {
      if (internals.unmounted) return;
      internals.unmounted = true;
      internals.mount?.unmount();
      internals.messenger.destroy();
    },
    open(): void {
      // Delega en el modo de montaje. No-op en embedded (sin overlay); abre
      // el overlay en modal.
      internals.mount?.open?.();
    },
    close(): void {
      // Delega en el modo de montaje. No-op en embedded; cierra el overlay.
      internals.mount?.close?.();
    },
    update(partial: Partial<BrickSettings>): void {
      if (internals.unmounted) return;
      const prev = internals.settings;
      const next = mergeSettings(prev, partial);
      internals.settings = next;

      // `brand`, `product`, `analytics`, `flow` o `availableFlows` afectan la
      // URL del iframe: recargan en lugar de postMessage. Reiniciamos el
      // handshake del messenger (sin destruirlo) para que el iframe recargado
      // vuelva a recibir `finva:init` con los nuevos valores, pero conservando
      // las suscripciones del mount (`finva:resize`/`finva:close`).
      // `iframe.contentWindow` es estable entre navegaciones (WindowProxy), así
      // que el filtro `event.source === iframe.contentWindow` sigue válido.
      if (
        next.brand !== prev.brand ||
        !productEqual(next.product, prev.product) ||
        !analyticsEqual(next.analytics, prev.analytics) ||
        !flowEqual(next.flow, prev.flow) ||
        !flowsEqual(next.availableFlows, prev.availableFlows)
      ) {
        const newInit = buildInitPayload(finva, next);
        const newSrc = buildIframeUrl(finva.origin, newInit, next.analytics, {
          flow: next.flow,
          availableFlows: next.availableFlows,
        });
        internals.messenger.resetHandshake(newInit);
        internals.iframe.setAttribute('src', newSrc);
        return;
      }

      const updatePayload: UpdatePayload = {};
      if (next.theme !== prev.theme) updatePayload.theme = next.theme;
      if (next.primaryColor !== prev.primaryColor) updatePayload.primaryColor = next.primaryColor;
      if (next.secondaryColor !== prev.secondaryColor) updatePayload.secondaryColor = next.secondaryColor;
      if (next.prefill !== prev.prefill) updatePayload.prefill = next.prefill;
      if (Object.keys(updatePayload).length > 0) {
        internals.messenger.send({ type: 'finva:update', payload: updatePayload });
      }
    },
  };
}

/** Construye el `InitPayload` (fuente de verdad para el iframe) desde la instancia + settings. */
function buildInitPayload(finva: Finva, s: NormalizedSettings): InitPayload {
  return {
    host: finva.host,
    brand: s.brand,
    mode: s.mode,
    theme: s.theme,
    primaryColor: s.primaryColor,
    secondaryColor: s.secondaryColor,
    locale: finva.locale,
    prefill: s.prefill,
    product: s.product,
  };
}

/**
 * Construye la URL del iframe con query params (§3.5).
 * `host` (origin de la página host) va si se detectó: el servidor lo usa para
 * resolver el tenant + CSP frame-ancestors. El resto de params se omite si está
 * vacío. `embedded=1` siempre al final. Los campos de `product` viajan como
 * `moto*` / `enganche` / `plazo` para que el iframe los lea en SSR
 * (searchParams) sin esperar el handshake. Los IDs de tracking (`analytics`)
 * viajan como `metaPixelId` / `googleAdsId`: son públicos (viven en el HTML del
 * merchant), así que pueden ir en la URL sin riesgo y el iframe los inyecta en
 * el HTML SSR para que disparen antes del handshake.
 *
 * `flow` / `availableFlows` viajan como `flow=` / `flows=` (comma-joined). Van
 * por query (NO en `InitPayload`) para no tocar el contrato congelado postMessage.
 *
 * The campaign params (`utm_*`, `fbclid`, `gclid`) are not settings: they come
 * from the host page itself, so an ad click that lands on the merchant's site
 * stays attributed once the client reaches the iframe. `getCampaignParams`
 * prefers the current URL and falls back to what was captured when the script
 * loaded, which is what survives the merchant's own navigation.
 */
function buildIframeUrl(
  origin: string,
  init: InitPayload,
  analytics?: { metaPixelId?: string; googleAdsId?: string },
  flow?: { flow?: string; availableFlows?: string[] },
): string {
  const params: string[] = [];
  if (init.host) params.push(`host=${encodeQueryParam(init.host)}`);
  if (init.brand) params.push(`brand=${encodeQueryParam(init.brand)}`);
  if (init.theme) params.push(`theme=${encodeQueryParam(init.theme)}`);
  if (init.primaryColor) params.push(`primaryColor=${encodeQueryParam(init.primaryColor)}`);
  if (init.secondaryColor) params.push(`secondaryColor=${encodeQueryParam(init.secondaryColor)}`);
  if (init.locale) params.push(`locale=${encodeQueryParam(init.locale)}`);
  const p = init.product;
  if (p) {
    params.push(`motoBrand=${encodeQueryParam(p.brand)}`);
    if (p.model) params.push(`motoModel=${encodeQueryParam(p.model)}`);
    if (p.slug) params.push(`motoSlug=${encodeQueryParam(p.slug)}`);
    if (p.year != null) params.push(`motoYear=${String(p.year)}`);
    // Sin `price` (el selector de catálogo lo resuelve) NO se emite el param:
    // `String(undefined)` mandaba un literal "motoPrice=undefined" a la URL.
    if (p.price != null) params.push(`motoPrice=${String(p.price)}`);
    if (p.finvaMotorcycleId) params.push(`motoFinvaId=${encodeQueryParam(p.finvaMotorcycleId)}`);
    if (p.downPayment != null) params.push(`enganche=${String(p.downPayment)}`);
    if (p.months != null) params.push(`plazo=${String(p.months)}`);
  }
  if (analytics?.metaPixelId) params.push(`metaPixelId=${encodeQueryParam(analytics.metaPixelId)}`);
  if (analytics?.googleAdsId) params.push(`googleAdsId=${encodeQueryParam(analytics.googleAdsId)}`);
  if (flow?.flow) params.push(`flow=${encodeQueryParam(flow.flow)}`);
  if (flow?.availableFlows && flow.availableFlows.length > 0) {
    params.push(`flows=${encodeQueryParam(flow.availableFlows.join(','))}`);
  }
  const campaign = getCampaignParams();
  for (const key of CAMPAIGN_KEYS) {
    const value = campaign[key];
    if (value) params.push(`${key}=${encodeQueryParam(value)}`);
  }
  params.push('embedded=1');
  return `${origin}/aplicacion?${params.join('&')}`;
}

/**
 * Conecta los eventos del iframe a los callbacks del brick. Los callbacks se
 * leen *live* vía `getSettings()` (no se capturan), así `controller.update`
 * que cambie callbacks se refleja sin re-cablear.
 */
function wireCallbacks(messenger: Messenger, getSettings: () => NormalizedSettings): void {
  messenger.on('finva:ready', () => getSettings().callbacks.onReady());
  messenger.on('finva:submit', (payload) => {
    const data = (payload as { data?: unknown } | null)?.data;
    getSettings().callbacks.onSubmit(data as Parameters<NormalizedCallbacks['onSubmit']>[0]);
  });
  messenger.on('finva:approved', (payload) => {
    const solicitud = (payload as { solicitud?: string } | null)?.solicitud ?? '';
    getSettings().callbacks.onApproved(solicitud);
  });
  messenger.on('finva:error', (payload) => {
    const error = (payload as { error?: { message: string; code?: string } } | null)?.error;
    if (error) getSettings().callbacks.onError(error);
  });
}